import React, { useEffect, useState } from "react";
import Header from "./Header.js";
import VertNav from "./VertNav.js";
import ES from "../../assets/ES.png";
import config from "../../config.js";

const ExpenseSetting = () => {
    const predefinedCategories = [
        "Purchase Invoice",
        "Licenses & Permissions",
        "Marketing",
        "Utilities",
        "Insurance",
        "Miscellaneous Expenses",
        "Payroll",
        "Rent",
        "Wages",
        "Leasing",
        "New Equipment",
        "Salon Space",
        "Training",
        "Website",
        "Software",
        "Card Processing Fees",
        "Advance Salary",
    ];

    const [newCategories, setNewCategories] = useState([]);
    const [apiCategories, setApiCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const bid = localStorage.getItem("branch_id");

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${config.apiUrl}/api/swalook/expense_category/?branch_name=${bid}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Token ${token}`,
                        },
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    if (data.data && Array.isArray(data.data)) {
                        const categories = data.data.flatMap((item) => {
                            if (item.vendor_expense_type) {
                                try {
                                    const cleanedString =
                                        item.vendor_expense_type
                                            .replace(/'/g, '"')
                                            .replace(/\\"/g, '"');
                                    return JSON.parse(cleanedString) || [];
                                } catch (e) {
                                    console.error(
                                        "Error parsing categories:",
                                        e
                                    );
                                    const matches =
                                        item.vendor_expense_type.match(
                                            /\[(.*?)\]/
                                        );
                                    if (matches && matches[1]) {
                                        return matches[1]
                                            .split(",")
                                            .map((cat) =>
                                                cat
                                                    .trim()
                                                    .replace(/^['"]|['"]$/g, "")
                                            );
                                    }
                                    return [];
                                }
                            }
                            return [];
                        });
                        setApiCategories([...new Set(categories)]);
                    }
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [bid]);

    const handleAddInput = () => {
        setNewCategories([...newCategories, { id: Date.now(), value: "" }]);
    };

    const handleInputChange = (id, value) => {
        setNewCategories(
            newCategories.map((item) =>
                item.id === id ? { ...item, value } : item
            )
        );
    };

    const handleRemoveInput = (id) => {
        setNewCategories(newCategories.filter((item) => item.id !== id));
    };

    const handleSaveCategories = async () => {
        const categoriesToSave = newCategories
            .filter((item) => item.value.trim() !== "")
            .map((item) => item.value)
            .filter(
                (value) =>
                    !predefinedCategories.includes(value) &&
                    !apiCategories.includes(value)
            );

        if (categoriesToSave.length === 0) {
            alert("No valid new categories to save");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${config.apiUrl}/api/swalook/expense_category/?branch_name=${bid}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${token}`,
                    },
                    body: JSON.stringify({
                        vendor_expense_type: categoriesToSave,
                    }),
                }
            );

            if (response.ok) {
                setApiCategories((prev) => [
                    ...new Set([...prev, ...categoriesToSave]),
                ]);
                setNewCategories([]);
                alert("Categories saved successfully!");
            } else {
                const errorData = await response.json();
                console.error("Error response:", errorData);
                alert("Failed to save categories");
            }
        } catch (error) {
            console.error("Error saving categories:", error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100">
            <Header />
            <VertNav />

            <div className="ml-0 md:ml-80 p-4 md:p-8">
                <h1 className="text-2xl font-bold text-gray-800 mb-8">
                    Expense Category Settings
                </h1>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Left Section - Image */}
                    <div className="w-full lg:w-1/3 bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
                        <img
                            src={ES}
                            alt="Expense Category Illustration"
                            className="w-64 h-64 object-contain mb-4"
                        />
                        <p className="text-lg font-medium text-gray-700">
                            Expense Category Settings
                        </p>
                    </div>

                    {/* Right Section - Categories */}
                    <div className="w-full lg:w-2/3 bg-white p-6 rounded-lg shadow-md">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">
                            Available Categories ({apiCategories.length})
                        </h2>

                        {loading ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2 mb-8">
                                {apiCategories.map((category, idx) => (
                                    <span
                                        key={idx}
                                        className="px-3 py-1 rounded-full text-sm font-medium
                                            bg-purple-100 text-purple-800 border border-purple-300"
                                    >
                                        {category}
                                        <span className="ml-1 text-purple-600">
                                            ✓
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="border-t pt-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                Add New Categories
                            </h2>

                            {newCategories.map((category) => (
                                <div
                                    key={category.id}
                                    className="flex items-center gap-2 mb-3"
                                >
                                    <input
                                        type="text"
                                        list={`category-options-${category.id}`}
                                        placeholder="Select or Add Category"
                                        value={category.value}
                                        onChange={(e) =>
                                            handleInputChange(
                                                category.id,
                                                e.target.value
                                            )
                                        }
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <datalist
                                        id={`category-options-${category.id}`}
                                    >
                                        {predefinedCategories.map(
                                            (predefinedCategory, idx) => (
                                                <option
                                                    key={idx}
                                                    value={predefinedCategory}
                                                >
                                                    {predefinedCategory}
                                                </option>
                                            )
                                        )}
                                    </datalist>
                                    <button
                                        onClick={() =>
                                            handleRemoveInput(category.id)
                                        }
                                        className="p-2 text-red-500 hover:text-red-700"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}

                            <div className="flex flex-wrap gap-4 mt-6">
                                <button
                                    onClick={handleAddInput}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                                >
                                    + New Category
                                </button>
                                <button
                                    onClick={handleSaveCategories}
                                    className={`px-4 py-2 text-white rounded-md transition-colors
                                        ${
                                            newCategories.some(
                                                (c) => c.value.trim() !== ""
                                            )
                                                ? "bg-green-500 hover:bg-green-600"
                                                : "bg-gray-400 cursor-not-allowed"
                                        }`}
                                    disabled={
                                        !newCategories.some(
                                            (c) => c.value.trim() !== ""
                                        )
                                    }
                                >
                                    Save Categories
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseSetting;
